import { useDispatch } from 'react-redux';
import { Box, Grid, Tabs } from '@mui/material';
import { useEffect, useState } from 'react';
import { Loader, P1R, SomethingWentWrongState } from 'src/components';
import { DashboardWrapper } from 'src/containers/wrappers';
import {
  selectAcceptTenantInviteSuccess,
  selectTenantsForSharedUserError,
  selectTenantsForSharedUserLoading,
  selectTenantsForSharedUsers,
} from 'src/redux/tenants/selectors';
import { useSelector } from 'react-redux/es/hooks/useSelector';
import { acceptTenantInvite, loadTenantsForSharedUser } from 'src/redux/tenants/actions';
import { useTranslation } from 'react-i18next';
import ContractorCard from './ContractorCard';
import ContractorsEmpty from './ContractorsEmpty';
import { PendingContractorsTab, StyledTab, TabsContainer } from './styled';
import AcceptContractorNotification from './AcceptContractorNotification';

type TabPanelPropsType = {
  index: number;
  value: number;
  subtitle?: string;
  children?: React.ReactNode;
};

function TabPanel(props: TabPanelPropsType) {
  const { children, value, index, ...other } = props;

  return (
    <div role="tabpanel" hidden={value !== index} id={`simple-tabpanel-${index}`} aria-labelledby={`simple-tab-${index}`} {...other}>
      {value === index && <Box sx={{ padding: '24px 0' }}>{children}</Box>}
    </div>
  );
}

function MyContractorsContainer() {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [tabValue, setTabValue] = useState(0);
  const myTenants = useSelector(selectTenantsForSharedUsers);
  const [isSuccessOpen, setIsSuccessOpen] = useState(false);
  const isTenantsLoading = useSelector(selectTenantsForSharedUserLoading);
  const isTenantsLoadingError = useSelector(selectTenantsForSharedUserError);
  const isAcceptTenantInviteSuccess = useSelector(selectAcceptTenantInviteSuccess);

  const pendingTenants = myTenants.filter((tenant) => !tenant.isAccepted);
  const acceptedTenants = myTenants.filter((tenant) => tenant.isAccepted);

  useEffect(() => {
    if (!myTenants.length) {
      dispatch(loadTenantsForSharedUser.init());
    }
  }, []);

  useEffect(() => {
    if (isAcceptTenantInviteSuccess) {
      setIsSuccessOpen(true);
      dispatch(loadTenantsForSharedUser.init());
      dispatch(acceptTenantInvite.reset());
    }
  }, [isAcceptTenantInviteSuccess]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const onAcceptHandler = (id: number) => {
    dispatch(acceptTenantInvite.init({ id }));
  };

  const onCloseAcceptSuccess = () => {
    setIsSuccessOpen(false);
  };

  if (isTenantsLoadingError) {
    return (
      <DashboardWrapper breadcrumbs={[[t('contractorsPage.title')]]}>
        <SomethingWentWrongState />
      </DashboardWrapper>
    );
  }

  return (
    <DashboardWrapper breadcrumbs={[[t('contractorsPage.title')]]}>
      <TabsContainer>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="basic tabs example">
          <StyledTab label={t('contractorsPage.accepted')} />
          <StyledTab label={`${t('contractorsPage.pending')} (${pendingTenants.length})`} />
        </Tabs>
      </TabsContainer>
      {isTenantsLoading ? (
        <Loader />
      ) : (
        <>
          <TabPanel value={tabValue} index={0}>
            {acceptedTenants.length > 0 ? (
              <Grid container spacing={2} alignItems="stretch">
                {acceptedTenants.map((tenant) => {
                  return (
                    <Grid key={tenant.id} item xs={12} sm={6} md={4} lg={3}>
                      <ContractorCard tenant={tenant} onAccept={onAcceptHandler} />
                    </Grid>
                  );
                })}
              </Grid>
            ) : (
              <ContractorsEmpty title={t('contractorsPage.noContractorsTitle')} description={t('contractorsPage.noAcceptedContractorsDescription')} />
            )}
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            {pendingTenants.length > 0 ? (
              <PendingContractorsTab>
                <P1R sx={{ maxWidth: '650px' }}>{t('contractorsPage.pendingContractorsDescription')}</P1R>
                <Grid container spacing={2} alignItems="stretch">
                  {pendingTenants.map((tenant) => {
                    return (
                      <Grid key={tenant.id} item xs={12} sm={6} md={4} lg={3}>
                        <ContractorCard tenant={tenant} onAccept={onAcceptHandler} />
                      </Grid>
                    );
                  })}
                </Grid>
              </PendingContractorsTab>
            ) : (
              <ContractorsEmpty title={t('contractorsPage.noContractorsTitle')} description={t('contractorsPage.noPendingContractorsDescription')} />
            )}
            <AcceptContractorNotification isSuccessOpen={isSuccessOpen} onCloseAcceptSuccess={onCloseAcceptSuccess} />
          </TabPanel>
        </>
      )}
    </DashboardWrapper>
  );
}

export default MyContractorsContainer;
